/**
 * The brief: what a person copies into any model to get something that works as a card.
 *
 * Nothing here calls a model. The brief is text anyone can read (the + sheet's "Make one" tab
 * shows it and copies it); the person asks their own model, pastes the answer back, and it goes
 * through the Upload tab's html path: the bench, a person's look on a phone, the fit check.
 *
 * The page brief is written from the html viewer's rules, not beside them: the script and
 * style hosts below are the viewer's CSP list (tests/unit/brief.test.ts parses
 * public/cards/html/index.html and fails if the two drift), the 4 KB is the save channel's cap
 * (src/lib/cardState.ts), and the screen is the card's frame in the feed: on the 375x667 phone
 * every card is reviewed on, the top bar, the meta strip and the bottom bar leave the page
 * 375x511, and the rail (Like, Comments, Share) sits over its right 72 px from the middle down
 * (tests/e2e/make-one.spec.ts measures both). A page that follows it carries
 * <meta name="blitz-brief" content="<version>">, so the readout can split the fit pass rate by
 * brief version.
 *
 * Version 2 (26 Sep): version 1 opened "Make me one web page for Blitz, a feed of what people
 * make with AI", and models built the feed: their own header, Like and Remix buttons under the
 * feed's own rail, swiping between items. Version 2 asks for one standalone thing and says what
 * Blitz already draws around it.
 */
import { STATE_MAX_BYTES } from './cardState';

/** Bumped whenever the words change; pages stamp it in their <head>. */
export const BRIEF_VERSION = 2;

/** Script hosts the html viewer's CSP allows (script-src), in the order it lists them. */
export const BRIEF_SCRIPT_HOSTS = [
  'cdnjs.cloudflare.com',
  'cdn.jsdelivr.net',
  'unpkg.com',
  'esm.sh',
  'cdn.skypack.dev',
  'ga.jspm.io',
] as const;

/** Stylesheet hosts the html viewer's CSP allows (style-src), in the order it lists them. */
export const BRIEF_STYLE_HOSTS = ['fonts.googleapis.com', 'cdnjs.cloudflare.com', 'cdn.jsdelivr.net', 'unpkg.com'] as const;

export const PAGE_BRIEF = `Make me one small thing to play with or look at: a toy, a tiny game, a moving picture, an interactive poster. Make it as a single web page, and reply with the complete page as one self-contained .html file and nothing else.

Where it goes
- It will be one card in Blitz, a phone feed of things people make with AI. Blitz draws everything around the card: the title, the maker's name, Like, Comments, Share and the button to the next card.
- So the page is only the thing itself. One thing, not a feed, a gallery, a list or tabs: nothing to swipe or page through.
- None of Blitz's own parts: no Like, Share, Comment, Follow or Remix buttons; no usernames, avatars, like or view counts; no logo, header, menu or title bar.
- Moving on is Blitz's job: do not use a vertical swipe to change what is shown. Drags and swipes that are part of the thing itself (drawing, flinging, steering) are fine.
- Blitz's buttons sit over the right-hand edge of the lower half: keep the right 72 px, from the middle down, free of anything to tap or read.

Screen
- It fills whatever viewport it is given, exactly: about 375x510 on a small phone, taller on bigger ones. Size everything from the viewport (100vw and 100vh, or innerWidth and innerHeight), never from a fixed height. No page scroll, no horizontal overflow, nothing cut off.
- Dark ground: background #0b0e14.

Feel
- It does something on its own within 1 s of loading. It is seen before it is touched.
- It answers the first touch within 100 ms.
- Touch-first: pointer events; touch-action: none on anything that handles drags; no hover-only affordances; no long-press menus.

Rules
- No network calls of its own: no fetch, XMLHttpRequest or WebSocket.
- Scripts and styles inline. The only external scripts allowed are from ${BRIEF_SCRIPT_HOSTS.join(', ')}; the only external stylesheets from ${BRIEF_STYLE_HOSTS.join(', ')}. Images as data: URLs or https.
- No <form>, no <iframe>, no <base>, no meta refresh, no navigation or links away.

Memory
- If window.blitz exists, restore from window.blitz.load() at start and call window.blitz.save(state) whenever state changes; keep state under ${STATE_MAX_BYTES / 1024} KB. It must also work when window.blitz does not exist.

Head
- A short <title> that names it: Blitz shows it as the card's title.
- <meta name="blitz-brief" content="${BRIEF_VERSION}"> in the <head>.

Size
- Under 200 KB is ideal; 25 MB is the most it can be.`;

export const IMAGE_BRIEF = `Make me one image for Blitz, a phone feed of things people make with AI.
- Portrait: 9:16 or 3:4.
- PNG, JPEG or WebP, under 50 MB.
- It should read at a glance on a 375-wide phone screen.
- Just the picture: no app screens, usernames, like counts or captions laid out like a post.`;

export const CLIP_BRIEF = `Make me one video clip for Blitz, a phone feed of things people make with AI.
- Portrait video: MP4 (H.264) or WebM.
- 30 seconds at most.
- Made to work muted: it starts without sound, so nothing depends on hearing it.
- Just the video: no app screens, usernames, like counts or captions laid out like a post.`;

export const BRIEFS = { page: PAGE_BRIEF, image: IMAGE_BRIEF, clip: CLIP_BRIEF } as const;

/**
 * What a model gave back, as the page to upload. Chat answers usually wrap the file in a
 * Markdown fence (```html … ```) and often add a sentence before or after it; the page is the
 * first fenced block that holds markup. A fence the answer never closed (a reply cut off
 * mid-file) is taken to the end. Anything without a fence is the page as pasted.
 */
export function unfence(text: string): string {
  const fence = /```[\w-]*[^\S\n]*\n([\s\S]*?)(?:\n?[^\S\n]*```|$)/g;
  for (let m = fence.exec(text); m; m = fence.exec(text)) {
    if (m[1].includes('<')) return m[1].trim();
  }
  return text.trim();
}

/** The page's own <title>, tidied for a card (one line, at most 80 characters), or undefined. */
export function titleOf(html: string): string | undefined {
  const m = /<title[^>]*>([\s\S]*?)<\/title>/i.exec(html);
  if (!m) return undefined;
  const t = m[1]
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
  return t ? t.slice(0, 80) : undefined;
}
